<script setup lang="ts">
import { mdiLock } from "@mdi/js";
import EventPriceIndicator from "../SportsBook/EventPriceIndicator.vue";
import { betwayBoostedOdds } from "~/assets/betwayIcons";
import { useBetbook } from "~/store/betbook";
import { useBetslip } from "~/store/betslip";
import { IOutcome } from "~/types/sports/outcome";
import { IMarket } from "~/types/sports/market";
import { IEvent } from "~/types/sports/event";

interface Props {
	eventId: number;
	outcome: IOutcome;
	market: IMarket;
	isSimpleView?: boolean;
	buildABetSelections: string[];
	selectedMarket: string | null;
	isPriceOnly?: boolean;
}

const betslip = useBetslip();
const store = useBetbook();
const props = defineProps<Props>();
const emits = defineEmits(["handleBuildABetSelection"]);
const lastPrice = ref<number>();
const priceAction = ref<"inc" | "dec">();
const event = computed(() => store.getEvent(props.eventId));
const price = computed(() => {
	try {
		return store.prices.get(props.outcome.outcomeId);
	} catch (e) {
		return null;
	}
});

const isDisabled = computed(() => {
	return (
		!price.value ||
		props.outcome.isTradingActive === false ||
		props.outcome.shouldDisplay === false ||
		price?.value.priceDecimal === 1 ||
		store.markets.get(props.outcome.originalMarketId)?.isActive === false ||
		!store.isEventActive(event.value as IEvent)
	);
});

const isBuildABet = computed(() => {
	return props.selectedMarket === "Build A Bet";
});

onMounted(() => {
	if (price.value) lastPrice.value = price.value.priceDecimal;
});

watchEffect(() => {
	if (lastPrice.value && price.value && lastPrice.value !== price.value.priceDecimal) {
		if (price.value.priceDecimal < lastPrice.value) {
			priceAction.value = "dec";
		} else if (price.value.priceDecimal > lastPrice.value) {
			priceAction.value = "inc";
		}
		lastPrice.value = price.value.priceDecimal;
		setTimeout(() => {
			priceAction.value = undefined;
		}, 2000);
	}
});
const route = useRoute();

const handleSelection = () => {
	const isEdit = route.query.isEditingBet === "true" ? true : false;
	const editOutcome: string = route.query.outcomeId as string;

	betslip.handleSelection(
		{ event: store.events.get(props.eventId), market: props.market, outcome: props.outcome, price: price.value, score: event.value.score },
		isEdit ? editOutcome : undefined
	);
};
</script>

<template>
	<div
		@click="
			(e) => {
				e.preventDefault();
				if (!isDisabled) {
					if (isBuildABet) {
						emits('handleBuildABetSelection', outcome.outcomeId);
					} else {
						handleSelection();
					}
				}
			}
		"
		:class="[
			isSimpleView
				? [
						'flex items-center justify-between h-10 px-[6px] gap-1 text-xs leading-3 overflow-hidden border rounded-md select-none font-bold',
						isDisabled
							? 'border-light-500 bg-light-500 dark:bg-dark-500 dark:border-dark-500'
							: (!isDisabled && !isBuildABet && betslip.isSelectionActive(outcome.outcomeId)) ||
							  (!isDisabled && isBuildABet && buildABetSelections.includes(outcome.outcomeId))
							? 'bg-identity border-identity text-light-50 cursor-pointer'
							: 'border-light-500 bg-light-50 dark:bg-dark-800 dark:border-dark-400 cursor-pointer',
				  ]
				: [
						'flex items-center justify-between h-12 px-2 gap-1 text-xs leading-3 overflow-hidden border rounded-md select-none',
						isDisabled
							? 'border-light-500 bg-light-500 dark:bg-dark-500 dark:border-dark-500'
							: (!isDisabled && !isBuildABet && betslip.isSelectionActive(outcome.outcomeId)) ||
							  (!isDisabled && isBuildABet && buildABetSelections.includes(outcome.outcomeId))
							? 'bg-identity border-identity text-light-50 cursor-pointer'
							: 'border-identity-100 bg-identity-50 dark:bg-dark-800 dark:border-dark-400 cursor-pointer',
				  ],
		]"
	>
		<div v-if="!isPriceOnly || (isPriceOnly && isBuildABet)" :class="isSimpleView ? 'overflow-hidden line-clamp-2 text-ellipsis' : ''">
			{{ outcome.displayName }} {{ outcome.sbv }}
		</div>
		<div
			v-if="!isDisabled && price && !isBuildABet"
			class="relative flex items-center font-bold h-7"
			:class="isPriceOnly ? 'w-full justify-center' : ''"
		>
			<EventPriceIndicator
				v-if="priceAction === 'inc'"
				class="absolute -translate-x-1/2 top-[2px] left-1/2"
				:indicator-class="[betslip.isSelectionActive(outcome.outcomeId) ? 'bg-light-50' : 'bg-primary-500']"
			/>
			<UiIcon class="w-4 h-4 fill-warning-600" :path="betwayBoostedOdds" v-if="outcome.isBoosted" />
			<span
				:class="[
					priceAction === 'dec' && 'text-error-500',
					priceAction === 'inc' && 'text-primary-500',
					betslip.isSelectionActive(outcome.outcomeId) && '!text-light-50',
				]"
			>
				{{ formatOdds(price.priceDecimal, price.numerator, price.denominator) }}
			</span>
			<EventPriceIndicator
				v-if="priceAction === 'dec'"
				class="absolute -translate-x-1/2 bottom-[2px] left-1/2 rotate-180"
				:indicator-class="[betslip.isSelectionActive(outcome.outcomeId) ? 'bg-light-50' : 'bg-error-500']"
			/>
		</div>
		<UiIcon v-else-if="!isBuildABet" class="h-4 dark:fill-light-500" :class="isPriceOnly ? 'w-full text-center' : 'w-4'" :path="mdiLock" />
	</div>
</template>
